export const getCurrentTimeString = () => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

export const resolveSelectionIndex = (items, entityId) => {
  if (!Array.isArray(items) || items.length === 0 || entityId == null) return 0;
  const matchIdx = items.findIndex((item) => `${item?.id}` === `${entityId}`);
  if (matchIdx >= 0) return matchIdx + 1;
  const numeric = Number(entityId);
  if (Number.isFinite(numeric) && numeric >= 0 && numeric <= items.length) {
    return numeric;
  }
  return 0;
};

export const normalizePrefillLabel = (value) => `${value || ''}`.trim().toLowerCase();

export const resolvePrefillEntityMatch = (items, entityId, prefillNameKey) => {
  const hasNamePrefill = !!prefillNameKey;
  const indexById = Array.isArray(items)
    ? items.findIndex((item) => `${item?.id}` === `${entityId}`)
    : -1;
  const indexByName =
    hasNamePrefill && Array.isArray(items)
      ? items.findIndex((item) => normalizePrefillLabel(item?.name) === prefillNameKey)
      : -1;

  let index = -1;
  if (hasNamePrefill) {
    if (indexByName >= 0) {
      index = indexByName;
    } else if (indexById >= 0) {
      const currentNameKey = normalizePrefillLabel(items[indexById]?.name);
      if (currentNameKey === prefillNameKey) {
        index = indexById;
      }
    }
  } else {
    index = indexById;
  }

  return { hasNamePrefill, index, indexById, indexByName };
};

export const parseDurationSeconds = (value) => {
  if (Number.isFinite(value)) return Number(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.toLowerCase().endsWith('s') ? trimmed.slice(0, -1) : trimmed;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

export const parseMinutes = (value) => {
  if (Number.isFinite(value)) return Number(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

export const getTourDurationBreakdown = (tour) => {
  if (!tour) return { seconds: 0, source: 'none' };

  const fromTourTotal = parseDurationSeconds(
    tour.totalDuration ?? tour.duration ?? tour.total_duration,
  );
  if (Number.isFinite(fromTourTotal) && fromTourTotal > 0) {
    return { seconds: fromTourTotal, source: 'tour.totalDuration' };
  }

  if (Array.isArray(tour.legs) && tour.legs.length) {
    const fromLegs = tour.legs.reduce((sum, leg) => {
      const seconds = parseDurationSeconds(leg?.durationSeconds ?? leg?.duration);
      return sum + (seconds ?? 0);
    }, 0);
    if (fromLegs > 0) return { seconds: fromLegs, source: 'tour.legs' };
  }

  if (Array.isArray(tour.nodes) && tour.nodes.length) {
    const fromNodeDurations = tour.nodes.reduce((sum, node) => {
      const segmentMinutes = parseMinutes(node?.segmentTime);
      const segmentSeconds = Number.isFinite(segmentMinutes) ? segmentMinutes * 60 : null;
      const seconds = parseDurationSeconds(
        node?.leg?.durationSeconds ?? node?.durationSeconds ?? node?.duration ?? segmentSeconds,
      );
      return sum + (seconds ?? 0);
    }, 0);
    if (fromNodeDurations > 0) {
      return { seconds: fromNodeDurations, source: 'tour.nodes.durationOrSegmentTime' };
    }

    const totalMinutesFromStart = tour.nodes.reduce((maxMinutes, node) => {
      const nodeMinutes = parseMinutes(
        node?.totalTimeFromStart ?? node?.timing?.totalMinutesFromStart,
      );
      if (!Number.isFinite(nodeMinutes)) return maxMinutes;
      return Math.max(maxMinutes, nodeMinutes);
    }, 0);
    if (totalMinutesFromStart > 0) {
      return { seconds: totalMinutesFromStart * 60, source: 'tour.nodes.totalTimeFromStart' };
    }
  }

  return { seconds: 0, source: 'none' };
};

export const getTotalDurationOfTour = (tour) => getTourDurationBreakdown(tour).seconds;

export const getDurationMinutesBetweenTimes = (start, end) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMin = sh * 60 + sm;
  let endMin = eh * 60 + em;
  if (endMin < startMin) endMin += 24 * 60;
  return endMin - startMin;
};

export const getEndTimeFromStartAndDurationSeconds = (start, durationSeconds) => {
  if (typeof start !== 'string' || !start.includes(':')) return '';
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return '';
  const [h, m] = start.split(':').map(Number);
  const startDate = new Date(2000, 0, 1, h, m, 0);
  const endDate = new Date(startDate.getTime() + durationSeconds * 1000);
  const endHour = String(endDate.getHours()).padStart(2, '0');
  const endMinute = String(endDate.getMinutes()).padStart(2, '0');
  return `${endHour}:${endMinute}`;
};
